/**
 * Plug Commission API handler
 * Provides functions for Matter plug commissioning, device list, on/off, power, and updates.
 */

import axios from 'axios';
import { endpointLink } from '../../index';

export interface ChargerInfoDevice {
  NodeID: string;
  BuildingID: string;
  PlugNum: number;
  /** Optional user-facing plug name; null/absent => UI default "<BuildingID>-<PlugNum>". */
  Name?: string | null;
  ChargeStart?: string | null;
  ChargeEnd?: string | null;
  Status?: string | null;
  Enabled?: number;
  Available?: number;
  OnOff?: number | null;
  PairingCode?: string | null;
  LastRx?: string | null;
  WifiName?: string | null;
  CreatedAt?: string | null;
  /** True if device responded recently (within ~90s) */
  reachable?: boolean;
  /** 'Reachable' | 'Likely Unplugged' */
  status?: string;
}

export interface MatterPowerMeasurements {
  activePower?: number;
  voltage?: number;
  current?: number;
}

export interface CommissionParams {
  PairingCode: string;
  plugNum?: number;
  BuildingID: string;
  WifiName: string;
}

export interface WifiConfigItem {
  WifiName: string;
  ssid: string;
}

export interface WifiConfigStatus {
  configured: boolean;
  ssid?: string;
}

export interface WifiConfigSaveParams {
  WifiName: string;
  SSID: string;
  password: string;
}

export interface CommissionResult {
  success: boolean;
  message: string;
  accepted?: boolean;
  nodeId?: string;
  plugNum?: number;
  requestId?: string;
}

export interface DiscoveryListUploadPlug {
  plugNumber: number;
  QPairingCode?: string | null;
  pairingCode?: string | null;
  plugName?: string | null;
}

export interface DiscoveryListUploadResult {
  success: boolean;
  buildingID: string;
  replaceExisting: boolean;
  inserted: number;
  updated: number;
  invalid: Array<{ pairingCode: string; error: string }>;
  invalidCount: number;
}

export interface DiscoveryCandidate {
  id: number;
  label: string | null;
  buildingID?: string | null;
  plugNum?: number | null;
  pairingCode?: string;
  pairingCodeMasked: string;
  qPairingCode?: string | null;
  confidence: 'high' | 'medium' | 'low' | 'ambiguous';
}

export interface DiscoveryWatcherStatus {
  running: boolean;
  buildingID: string | null;
  startedAt: string | null;
  lastEventAt: string | null;
  watchedCodes: number;
  pid: number | null;
  visibleCount: number;
  // Snapshot of currently visible matched candidates, for hydrating
  // `lastFinderCandidate` on page load without waiting for the next
  // mDNS announce.
  visibleCandidates?: DiscoveryCandidate[];
}

export interface DiscoveryListEntry {
  Id: number;
  BuildingID: string | null;
  PlugNum: number | null;
  PairingCode: string | null;
  QpairingCode: string | null;
  Discriminator: number | null;
  ShortDiscriminator: number | null;
  VendorID: number | null;
  Status: 'active' | 'consumed' | string;
  CommissionedNodeID: string | null;
  Consumed: number;
  AddedAt: string;
}

export interface SyncDevicesResult {
  success: boolean;
  apply: boolean;
  actions: {
    fixMatterOnly: boolean;
    fixDbOnly: boolean;
  };
  counts: {
    matterTotal: number;
    dbTotal: number;
    inSync: number;
    matterOnly: number;
    dbOnly: number;
  };
  diff: {
    matterOnly: string[];
    dbOnly: string[];
    inSync: string[];
  };
  fixed: {
    matterOnly: string[];
    dbOnly: string[];
  };
  errors: Array<{ nodeId: string; action: string; error: string }>;
  message: string;
}

export interface CommissioningAttempt {
  Id: number;
  RequestId: string;
  NodeID: string | null;
  PairingCode: string | null;
  BuildingID: string;
  WifiName: string | null;
  PlugNum: number | null;
  Status: 'commissioning_pending' | 'commissioned' | 'commissioning_failed' | string;
  ErrorMessage: string | null;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface DeviceUpdateParams {
  PlugNum?: number;
  BuildingID?: string;
  WifiName?: string;
  Enabled?: number;
  Available?: number;
}

export async function commissionDevice(params: CommissionParams): Promise<CommissionResult> {
  const { data } = await axios.post<CommissionResult>(`${endpointLink}plugs/commission`, params);
  return data;
}

export async function uploadDiscoveryList(params: {
  SiteID?: string;
  BuildingID: string;
  source?: string;
  replaceExisting?: boolean;
  Plugs: DiscoveryListUploadPlug[];
}): Promise<DiscoveryListUploadResult> {
  const { data } = await axios.post<DiscoveryListUploadResult>(`${endpointLink}plugs/discovery/list/upload`, params);
  return data;
}

export interface WifiSwitchResult {
  success: boolean;
  accepted: boolean;
  requestId: string;
  wifiName: string;
  count: number;
}

/**
 * Kick off a per-BuildingID WiFi switch for the given commissioned nodes.
 * Returns a requestId; per-device outcome streams over the 'plugs' websocket as
 * `wifiSwitchProgress` messages (keyed by requestId + nodeId).
 */
export async function switchWifi(nodeIds: string[], WifiName: string): Promise<WifiSwitchResult> {
  const { data } = await axios.post<WifiSwitchResult>(`${endpointLink}plugs/wifi-switch`, {
    WifiName,
    nodeIds,
  });
  return data;
}

export async function clearDiscoveryList(buildingID: string): Promise<{ cleared: number }> {
  const { data } = await axios.post<{ success: boolean; cleared: number }>(
    `${endpointLink}plugs/discovery/list/clear`,
    { BuildingID: buildingID }
  );
  return { cleared: data.cleared ?? 0 };
}

export async function getDiscoveryList(buildingID: string): Promise<DiscoveryListEntry[]> {
  const { data } = await axios.get<{ buildingID: string; entries: DiscoveryListEntry[] }>(
    `${endpointLink}plugs/discovery/list`,
    { params: { BuildingID: buildingID } }
  );
  return Array.isArray(data?.entries) ? data.entries : [];
}

export async function getDiscoveryStatus(): Promise<DiscoveryWatcherStatus> {
  const { data } = await axios.get<{ status: DiscoveryWatcherStatus }>(`${endpointLink}plugs/discovery/status`);
  return data.status;
}

export async function startDiscoveryWatch(buildingID: string): Promise<DiscoveryWatcherStatus> {
  const { data } = await axios.post<{ success: boolean; status: DiscoveryWatcherStatus }>(
    `${endpointLink}plugs/discovery/watch/start`,
    { BuildingID: buildingID }
  );
  return data.status;
}

export async function stopDiscoveryWatch(): Promise<DiscoveryWatcherStatus> {
  const { data } = await axios.post<{ success: boolean; status: DiscoveryWatcherStatus }>(
    `${endpointLink}plugs/discovery/watch/stop`
  );
  return data.status;
}

export async function commissionDiscoveryEntry(params: {
  listId: number;
  BuildingID: string;
  WifiName: string;
  plugNum?: number;
}): Promise<CommissionResult> {
  const { data } = await axios.post<CommissionResult>(`${endpointLink}plugs/discovery/commission`, params);
  return data;
}

export async function listDevices(buildingID?: string): Promise<ChargerInfoDevice[]> {
  const params = buildingID && buildingID !== 'all' ? { BuildingID: buildingID } : { BuildingID: 'all' };
  const { data } = await axios.get<{ devices: ChargerInfoDevice[] }>(
    `${endpointLink}plugs/devices`,
    { params }
  );
  return data.devices || [];
}

export async function listCommissioningAttempts(params?: {
  limit?: number;
  status?: string;
  BuildingID?: string;
  nodeId?: string;
  requestId?: string;
}): Promise<CommissioningAttempt[]> {
  const { data } = await axios.get<{ attempts: CommissioningAttempt[] }>(
    `${endpointLink}plugs/commissioning-attempts`,
    { params }
  );
  return Array.isArray(data?.attempts) ? data.attempts : [];
}

export async function syncMatterDevices(params?: {
  apply?: boolean;
  fixMatterOnly?: boolean;
  fixDbOnly?: boolean;
}): Promise<SyncDevicesResult> {
  const { data } = await axios.post<SyncDevicesResult>(
    `${endpointLink}plugs/sync`,
    {
      apply: params?.apply ?? true,
      fixMatterOnly: params?.fixMatterOnly ?? true,
      fixDbOnly: params?.fixDbOnly ?? true,
    }
  );
  return data;
}

export async function listBuildingIds(): Promise<string[]> {
  const { data } = await axios.get<string[]>(`${endpointLink}plugs/building-ids`);
  return Array.isArray(data) ? data : [];
}

export type CreateBuildingInput = {
  BuildingID: string;
  Timezone?: string;
  demandLimit?: number;
};

export type CreateBuildingResult = {
  success: boolean;
  BuildingID: string;
  runtimeStarted: boolean;
  note?: string;
};

/** Create a new building (setupConfig row) and start its runtime on the backend. */
export async function createBuilding(input: CreateBuildingInput): Promise<CreateBuildingResult> {
  const { data } = await axios.post<CreateBuildingResult>(`${endpointLink}buildings`, input);
  return data;
}

export type BuildingRenameStatus = {
  phase: 'idle' | 'validating' | 'updating' | 'refreshing' | 'finalizing' | 'restarting' | 'done' | 'error';
  running: boolean;
  oldBuildingID: string;
  newBuildingID: string;
  startedAt: string | null;
  finishedAt: string | null;
  message: string;
  updated: Record<string, number>;
  notes: string[];
  error: string | null;
  restartRequested: boolean;
};

export async function renameBuilding(
  oldBuildingID: string,
  newBuildingID: string,
  restart = true
): Promise<void> {
  await axios.post(`${endpointLink}buildings/${encodeURIComponent(oldBuildingID)}/rename`, {
    NewBuildingID: newBuildingID,
    restart,
  });
}

export async function getBuildingRenameStatus(): Promise<BuildingRenameStatus> {
  const { data } = await axios.get<BuildingRenameStatus>(`${endpointLink}buildings/rename/status`);
  return data;
}

export async function setDeviceOn(nodeId: string, endpointId?: number): Promise<void> {
  const params = endpointId != null ? { endpointId } : {};
  await axios.post(`${endpointLink}plugs/devices/${nodeId}/on`, null, { params });
}

export async function setDeviceOff(nodeId: string, endpointId?: number): Promise<void> {
  const params = endpointId != null ? { endpointId } : {};
  await axios.post(`${endpointLink}plugs/devices/${nodeId}/off`, null, { params });
}

export async function setAllPlugsOff(buildingID: string): Promise<void> {
  await axios.post(`${endpointLink}plugs/all-off`, { BuildingID: buildingID });
}

export async function setAllPlugsOn(buildingID: string): Promise<void> {
  await axios.post(`${endpointLink}plugs/all-on`, { BuildingID: buildingID });
}

export async function getDeviceOnOff(nodeId: string, endpointId?: number): Promise<{ on: boolean }> {
  const params = endpointId != null ? { endpointId } : {};
  const { data } = await axios.get<{ on: boolean }>(
    `${endpointLink}plugs/devices/${nodeId}/onoff`,
    { params }
  );
  return data;
}

export async function decommissionDevice(nodeId: string): Promise<void> {
  await axios.delete(`${endpointLink}plugs/devices/${nodeId}`);
}

export async function forgetDevice(nodeId: string): Promise<void> {
  await axios.delete(`${endpointLink}plugs/forget/${nodeId}`);
}

export async function getDevicePower(nodeId: string): Promise<MatterPowerMeasurements> {
  const { data } = await axios.get<MatterPowerMeasurements>(
    `${endpointLink}plugs/devices/${nodeId}/power`
  );
  return data;
}

export interface PlugBasicInformation {
  vendorName: string | null;
  vendorId: number | null;
  productName: string | null;
  productId: number | null;
  productLabel: string | null;
  nodeLabel: string | null;
  hardwareVersion: number | null;
  hardwareVersionString: string | null;
  softwareVersion: number | null;
  softwareVersionString: string | null;
  manufacturingDate: string | null;
  partNumber: string | null;
  productUrl: string | null;
  serialNumber: string | null;
  uniqueId: string | null;
}

export interface PlugStartUpOnOff {
  supported: boolean;
  /** 0=Off, 1=On, 2=Toggle, null=PreviousValue */
  value: number | null;
  /** 'Off' | 'On' | 'Toggle' | 'PreviousValue' | null */
  label: string | null;
}

export interface PlugOtaStatus {
  supported: boolean;
  updatePossible: boolean | null;
  updateState: number | null;
  updateStateLabel: string | null;
  updateStateProgress: number | null;
}

export interface PlugInspectionResult {
  nodeId: string;
  endpointId: number | null;
  basicInformation: PlugBasicInformation;
  startUpOnOff: PlugStartUpOnOff;
  ota: PlugOtaStatus;
  errors: string[];
}

export interface BulkPlugInspectionRow extends PlugInspectionResult {
  BuildingID: string;
  PlugNum: number | null;
  PairingCode: string | null;
  WifiName: string | null;
  LastRx: string | null;
  offline: boolean;
  error?: string;
}

export interface BulkPlugInspectionResult {
  buildingID: string;
  total: number;
  elapsedMs: number;
  concurrency: number;
  results: BulkPlugInspectionRow[];
}

export async function inspectDevice(
  nodeId: string,
  endpointId?: number,
): Promise<PlugInspectionResult> {
  const params = endpointId != null ? { endpointId } : {};
  const { data } = await axios.get<PlugInspectionResult>(
    `${endpointLink}plugs/devices/${nodeId}/inspect`,
    { params },
  );
  return data;
}

export async function inspectAllDevices(
  buildingID: string,
  concurrency?: number,
): Promise<BulkPlugInspectionResult> {
  const params: Record<string, string | number> = {
    BuildingID: buildingID || 'all',
  };
  if (concurrency != null) params.concurrency = concurrency;
  const { data } = await axios.get<BulkPlugInspectionResult>(
    `${endpointLink}plugs/inspect`,
    { params },
  );
  return data;
}

export async function updateDevice(
  nodeId: string,
  updates: DeviceUpdateParams
): Promise<void> {
  await axios.patch(`${endpointLink}plugs/devices/${nodeId}`, updates);
}

export async function getWifiConfigList(): Promise<WifiConfigItem[]> {
  const { data } = await axios.get<WifiConfigItem[]>(`${endpointLink}wifi-config/list`);
  return Array.isArray(data) ? data : [];
}

export async function getWifiConfig(wifiName: string): Promise<WifiConfigStatus> {
  const { data } = await axios.get<WifiConfigStatus>(`${endpointLink}wifi-config`, {
    params: { WifiName: wifiName },
  });
  return data;
}

export async function saveWifiConfig(params: WifiConfigSaveParams): Promise<{ success: boolean }> {
  const { data } = await axios.post<{ success: boolean }>(`${endpointLink}wifi-config`, params);
  return data;
}

export async function deleteWifiConfig(wifiName: string): Promise<{ success: boolean; deleted: string }> {
  const encoded = encodeURIComponent(wifiName);
  const { data } = await axios.delete<{ success: boolean; deleted: string }>(`${endpointLink}wifi-config/${encoded}`);
  return data;
}

/** Latest chargerData row for a building (from chargerData table). */
export interface ChargerDataRow {
  BuildingID: string;
  Timestamp: string;
  NCharging: number;
  NWaiting: number;
  CState: number[] | string;
  CPower: Array<number | null> | string;
  CEnergy: number[] | string;
  Layer?: number[] | string;
}

export async function getChargerDataByBuilding(buildingID: string): Promise<ChargerDataRow | null> {
  const { data } = await axios.get<ChargerDataRow[]>(`${endpointLink}charger-graph`, {
    params: { BuildingID: buildingID },
  });
  const rows = Array.isArray(data) ? data : [data];
  return rows.length > 0 ? rows[0] : null;
}

export interface NucMdnsStatus {
  success: boolean;
  hostname: string;
  mdnsHost: string;
}

export interface NucMdnsUpdateParams {
  hostname: string;
  sudoUsername?: string;
  sudoPassword?: string;
}

export interface NucMdnsUpdateResult extends NucMdnsStatus {
  message: string;
}

export async function getNucMdnsStatus(): Promise<NucMdnsStatus> {
  const { data } = await axios.get<NucMdnsStatus>(`${endpointLink}nuc/mdns`);
  return data;
}

export async function updateNucMdns(params: NucMdnsUpdateParams): Promise<NucMdnsUpdateResult> {
  const { data } = await axios.post<NucMdnsUpdateResult>(`${endpointLink}nuc/mdns`, params);
  return data;
}


export function fabricExportUrl(buildingID: string): string {
  const qp = encodeURIComponent(buildingID);
  return `${endpointLink}plugs/fabric/export?BuildingID=${qp}`;
}

/**
 * URL for downloading a building's commission list as an uploadable JSON file
 * ({ SiteID, BuildingID, Plugs: [...] }). Meant to be opened via a hidden
 * <a download> so the browser saves the attachment.
 */
export function commissionListDownloadUrl(buildingID: string): string {
  const qp = encodeURIComponent(buildingID);
  return `${endpointLink}plugs/discovery/list/download?BuildingID=${qp}`;
}

export interface FabricImportResult {
  success: boolean;
  importedBuildingID: string | null;
  counts: {
    chargerInfo: number;
    wifiConfig: number;
    chargerCommissionList: number;
  };
  backupPath: string | null;
  rolledBack: boolean;
  message: string;
}

export async function fabricImport(file: File): Promise<FabricImportResult> {
  // axios would wrap the File in a FormData/Blob shuffle that drops the
  // raw-bytes shape; use fetch so the backend's express.raw() sees the
  // archive exactly as uploaded.
  const res = await fetch(`${endpointLink}plugs/fabric/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/gzip' },
    body: file,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data && data.error) || `Fabric import failed (HTTP ${res.status})`);
  }
  return data as FabricImportResult;
}
